// AdSense の H5 Games Ads（adBreak）の口。VITE_ADSENSE_CLIENT を入れたときだけ使う。
// 未検証：AdSense の承認と H5 Games Ads の追加の申請が済むまで、本物の広告では確かめられない。
// 報酬は adViewed のときだけ付ける（adBreakDone は広告が出なくても呼ばれる）。
// H5 Games Ads が承認されるまで（h5 が false）は adBreak を呼ばない（承認なしで使うとポリシーの問題になりうる）。
// そのあいだ、全画面の広告は出さず、ごほうびは広告を見ずに渡す。帯の広告（bannerSlot）とタグの読み込みは、AdSense の承認だけで使える
import type { AdProvider } from "./types";

type AdBreakFn = (o: Record<string, unknown>) => void;
declare global {
  interface Window {
    adsbygoogle?: unknown[];
    adBreak?: AdBreakFn;
    adConfig?: AdBreakFn;
  }
}

const SRC = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";

export function createAdSense(client: string, opts: { childDirected: boolean; bannerSlot?: string; h5: boolean }): AdProvider {
  // ビルドしたページ（tools/build.mjs）は、head にもう同じタグ（AdSense の画面が出すコードと、下と同じ印）を持っている。
  // 二重に読まないように、あるときは足さない。無いとき（手元の npm run dev）は今までどおり足す
  if (!document.querySelector(`script[src^="${SRC}"]`)) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `${SRC}?client=${encodeURIComponent(client)}`;
    s.crossOrigin = "anonymous";
    s.dataset.adClient = client;
    // tools/lib/site-pages.mjs の ADSENSE_FREQUENCY_HINT と同じ
    s.dataset.adFrequencyHint = "90s";
    // 子ども向けの印（https://support.google.com/adsense/answer/3248194：非同期のコードは data-tag-for-age-treatment。1 が子ども向け）。
    // 関心に基づく広告とリマーケティングの広告が出なくなる
    if (opts.childDirected) s.setAttribute("data-tag-for-age-treatment", "1");
    if (import.meta.env.DEV) s.dataset.adbreakTest = "on";
    document.head.append(s);
  }
  window.adsbygoogle = window.adsbygoogle ?? [];
  const push: AdBreakFn = (o) => window.adsbygoogle!.push(o);
  if (opts.h5) {
    window.adBreak = window.adConfig = push;
    push({ preloadAdBreaks: "on", sound: "on" });
  }

  return {
    name: "adsense",
    interstitial({ gameId }) {
      if (!opts.h5) return Promise.resolve({ shown: false });
      return new Promise((resolve) => {
        let shown = false;
        push({
          type: "next",
          name: `${gameId}-next`,
          beforeAd: () => (shown = true),
          adBreakDone: () => resolve({ shown }),
        });
      });
    },
    rewarded({ gameId }) {
      if (!opts.h5) return Promise.resolve({ granted: true });
      return new Promise((resolve) => {
        let granted = false;
        push({
          type: "reward",
          name: `${gameId}-reward`,
          beforeReward: (showAdFn: () => void) => showAdFn(),
          adViewed: () => (granted = true),
          adDismissed: () => (granted = false),
          adBreakDone: () => resolve({ granted }),
        });
      });
    },
    mountBanner(el) {
      if (!opts.bannerSlot) {
        el.replaceChildren();
        return;
      }
      const ins = document.createElement("ins");
      ins.className = "adsbygoogle";
      ins.style.display = "block";
      ins.dataset.adClient = client;
      ins.dataset.adSlot = opts.bannerSlot;
      ins.dataset.adFormat = "auto";
      ins.dataset.fullWidthResponsive = "true";
      el.replaceChildren(ins);
      push({});
    },
  };
}
